import { OptionStatDto, QuestionStatsDto, SurveyQuestionTypeEnum } from '@families-link/shared';
import { Box, Divider, Typography, Tooltip as MuiTooltip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { EyeIcon } from '../../../_core/components/icons';
import { CoreIconButton } from '../../../_core/components/_ui/core-button';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export interface SurveyQuestionStatsProps {
	questionStat: QuestionStatsDto;
	onView?: (questionStat: QuestionStatsDto) => void;
}

const CustomTooltip = (props: any) => {
	const { active, payload, label } = props;

	if (!active || !payload || !payload.length) return null;

	return (
		<Box
			sx={{
				width: '100px',
				maxHeight: '170px',
				overflowY: 'auto',
				backgroundColor: 'white',
				border: '1px solid #ccc',
				borderRadius: '6px',
				padding: '10px',
				boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
				pointerEvents: 'auto',
				py: 2,
				px: 2,
			}}
		>
			<Typography sx={{ mb: '10px', fontWeight: 'bold', mt: 0 }}>{label}</Typography>
			{payload.map((entry: any, index: number) => (
				<Box
					key={index}
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: '8px',
					}}
				>
					<Box
						component="span"
						sx={{
							flex: 1,
							color: entry.color,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
						title={entry.name}
					>
						{entry.name}
					</Box>
					<Box component="span" sx={{ whiteSpace: 'nowrap' }}>
						{entry.value}
					</Box>
				</Box>
			))}
		</Box>
	);
};

export const SurveyQuestionStats: React.FC<SurveyQuestionStatsProps> = ({ questionStat, onView }) => {
	const { text, type, isRequired, totalAnswers, optionStats } = questionStat;
	const [t] = useTranslation();

	const handleViewQuestion = useCallback(() => {
		if (!questionStat) {
			return;
		}
		onView?.(questionStat);
	}, [onView, questionStat]);

	const chartData = optionStats?.length
		? optionStats?.map(({ optionValue, count }) => ({ optionValue, count }))
		: [{ optionValue: '', count: 0 }];
	const barSize = Math.max(30, Math.min(88, 600 / chartData.length));

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'stretch',
				borderRadius: '8px',
				border: '1px solid #DCDFE7',
				flexWrap: 'wrap',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					padding: 3,
					gap: 2.5,
					flex: '1 1 300px',
					minWidth: '250px',
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row' }}>
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 500,
							lineHeight: '120%',
						}}
					>
						Question name:
					</Typography>

					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 400,
							lineHeight: '120%',
							marginLeft: '4px',
						}}
					>
						{text}
					</Typography>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'row' }}>
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 500,
							lineHeight: '120%',
						}}
					>
						Question type:
					</Typography>
					{}
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 400,
							lineHeight: '120%',
							marginLeft: '4px',
						}}
					>
						{type}
					</Typography>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'row' }}>
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 500,
							lineHeight: '120%',
						}}
					>
						Required/Optional:
					</Typography>
					{}
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 400,
							lineHeight: '120%',
							marginLeft: '4px',
						}}
					>
						{isRequired ? 'Required' : 'Optional'}
					</Typography>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'row' }}>
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 500,
							lineHeight: '120%',
						}}
					>
						Answers amount:
					</Typography>
					{}
					<Typography
						variant="body2"
						sx={{
							fontSize: '14px',
							fontWeight: 400,
							lineHeight: '120%',
							marginLeft: '4px',
						}}
					>
						{totalAnswers}
					</Typography>
				</Box>
			</Box>

			<Divider orientation="vertical" flexItem sx={{ alignSelf: 'stretch' }} />

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					padding: '16px 24px',
					gap: 2,
					flex: '2 1 400px',
					minWidth: '300px',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontWeight: 500, fontSize: '16px' }}>
						Answers
					</Typography>
					<MuiTooltip title={t('buttons.tooltips.full-details')}>
						<CoreIconButton onClick={handleViewQuestion}>
							<EyeIcon sx={{ fontSize: 30 }} color="primary" />
						</CoreIconButton>
					</MuiTooltip>
				</Box>
				<Box sx={{ flexGrow: 1, width: '100%', minHeight: 160 }}>
					<ResponsiveContainer width="100%" height="100%">
						<BarChart data={chartData}>
							<CartesianGrid strokeDasharray="4 4" y={20} syncWithTicks />
							<XAxis
								dataKey="optionValue"
								tickSize={12}
								tick={({ x, y, payload, ...rest }) => (
									<text x={x} y={y + 12} textAnchor="middle" {...rest}>
										<tspan fontSize={12}>{payload.value}</tspan>
									</text>
								)}
							/>
							<YAxis
								allowDecimals={false}
								tick={({ x, y, payload, ...rest }) => (
									<text x={x} y={y + 5} textAnchor="end" fill="#F89377" {...rest}>
										<tspan fontSize={12}>{payload.value}</tspan>
									</text>
								)}
								padding={{ top: 20 }}
								tickSize={12}
							/>
							<Tooltip content={CustomTooltip} cursor={{ fill: 'transparent' }} />
							<Bar dataKey="count" fill="#78BBE2" radius={[10, 10, 0, 0]} barSize={barSize} />
						</BarChart>
					</ResponsiveContainer>
				</Box>
			</Box>
		</Box>
	);
};
